import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FilterPanel, type FiltersState, DEFAULT_FILTERS } from './FilterPanel';

const DEFAULT_RADIUS_KM = 25;

interface MoreFiltersDrawerProps {
  filters: FiltersState;
  onFiltersChange: (patch: Partial<FiltersState>) => void;
  priceMax?: number;
  activeCount: number;
  /** Display-only — server-resolved (student profile or geo header), never user-editable */
  currency?: string;
  hasLocation?: boolean;
  radiusKm?: number;
  onRadiusChange?: (km: number) => void;
  hideOfferingType?: boolean;
  sortItems?: { value: string; label: string }[];
  extraSections?: React.ReactNode;
  hideTopicKeywords?: boolean;
}

export function MoreFiltersDrawer({
  filters,
  onFiltersChange,
  priceMax,
  activeCount,
  currency,
  hasLocation = false,
  radiusKm = DEFAULT_RADIUS_KM,
  onRadiusChange,
  hideOfferingType,
  sortItems,
  extraSections,
  hideTopicKeywords,
}: MoreFiltersDrawerProps) {
  const [open, setOpen] = useState(false);
  // Local draft state — applied on "Apply", discarded on close without apply
  const [draft, setDraft] = useState<FiltersState>(filters);
  const [draftRadius, setDraftRadius] = useState<number>(radiusKm);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setDraft(filters);       // snapshot current filters when opening
      setDraftRadius(radiusKm); // snapshot current radius when opening
    }
    setOpen(isOpen);
  };

  const handleDraftChange = (patch: Partial<FiltersState>) => {
    setDraft(prev => ({ ...prev, ...patch }));
  };

  const handleApply = () => {
    onFiltersChange(draft);
    if (hasLocation) onRadiusChange?.(draftRadius);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft(DEFAULT_FILTERS);
    setDraftRadius(DEFAULT_RADIUS_KM);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-2 cursor-pointer transition-colors duration-150 hover:bg-accent"
        onClick={() => handleOpen(true)}
      >
        <SlidersHorizontal className="w-4 h-4" />
        Filters
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
            {activeCount}
          </span>
        )}
      </Button>

      <Sheet open={open} onOpenChange={handleOpen}>
        <SheetContent side="bottom" className="h-[90dvh] p-0 flex flex-col">
          <SheetHeader className="px-5 pt-5 pb-3 border-b">
            <SheetTitle className="text-base">Filters</SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1 px-5 py-4">
            <FilterPanel
              filters={draft}
              onFiltersChange={handleDraftChange}
              priceMax={priceMax}
              currency={currency}
              hasLocation={hasLocation}
              radiusKm={draftRadius}
              onRadiusChange={setDraftRadius}
              hideOfferingType={hideOfferingType}
              hideTopicKeywords={hideTopicKeywords}
              sortItems={sortItems}
              extraSections={extraSections}
            />
          </ScrollArea>

          <SheetFooter className="px-5 py-4 border-t flex gap-3">
            <Button
              variant="outline"
              className="flex-1 cursor-pointer"
              onClick={handleClear}
            >
              Clear all
            </Button>
            <SheetClose asChild>
              <Button
                className="flex-1 cursor-pointer"
                onClick={handleApply}
              >
                Apply filters
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
